import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { Modal } from "react-responsive-modal";
import cogoToast from "cogo-toast";
import Loader from "react-loader-spinner";
import epcisService from "../services/epcis-service";
import "react-responsive-modal/styles.css";
import "react-loader-spinner/dist/loader/css/react-spinner-loader.css";

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;

const showError = (message) => {
  const { hide } = cogoToast.error(message, {
    hideAfter: 15,
    onClick: () => hide(),
  });
};

const EpcisUploadModal = ({ open, onClose, onSuccess }) => {
  const { register, reset } = useForm({});
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileValid, setFileValid] = useState(false);

  const clearFileState = (inputEl) => {
    if (inputEl) inputEl.value = "";
    setSelectedFile(null);
    setFileValid(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    const inputEl = event.target;

    if (!file) {
      setSelectedFile(null);
      setFileValid(false);
      return;
    }

    // 1. Extension check
    if (!file.name.toLowerCase().endsWith(".xml")) {
      showError("Only .xml files are accepted. Please select a valid EPCIS XML file.");
      clearFileState(inputEl);
      return;
    }

    // 2. Size check
    if (file.size > MAX_FILE_SIZE_BYTES) {
      showError("File size exceeds 50 MB limit.");
      clearFileState(inputEl);
      return;
    }

    // 3. Empty check
    if (file.size === 0) {
      showError("Selected file is empty.");
      clearFileState(inputEl);
      return;
    }

    // 4. Content check — first 200 bytes must start with "<" (skip BOM if present)
    const reader = new FileReader();
    reader.onload = (e) => {
      const bytes = new Uint8Array(e.target.result);
      let startIdx = 0;
      if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) startIdx = 3; // UTF-8 BOM
      else if ((bytes[0] === 0xff && bytes[1] === 0xfe) || (bytes[0] === 0xfe && bytes[1] === 0xff)) startIdx = 2; // UTF-16 BOM

      if (String.fromCharCode(bytes[startIdx]) !== "<") {
        showError("File does not appear to be a valid XML document.");
        clearFileState(inputEl);
        return;
      }

      setSelectedFile(file);
      setFileValid(true);
    };
    reader.onerror = () => {
      showError("Could not read the selected file.");
      clearFileState(inputEl);
    };
    reader.readAsArrayBuffer(file.slice(0, 200));
  };

  const handleUpload = async () => {
    if (!selectedFile || !fileValid) return;

    try {
      setLoading(true);
      await epcisService.uploadEpcisFile(selectedFile);
      const { hide } = cogoToast.success("EPCIS File Uploaded Successfully!!", {
        hideAfter: 15,
        onClick: () => hide(),
      });
      reset({});
      setSelectedFile(null);
      setFileValid(false);
      onSuccess();
      onClose();
    } catch (e) {
      showError("Something went wrong! Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset({});
    setSelectedFile(null);
    setFileValid(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} center>
      <div className="pt-6 px-8 pb-6" style={{ minWidth: "380px" }}>
        <div
          className={`inline-block customtop z-50 absolute ${
            loading ? "show" : "hidden"
          }`}
        >
          <Loader
            type="Oval"
            color="#00B2A9"
            height={50}
            width={50}
            timeout={300000}
          />
        </div>
        <h3 className="text-labelclr font-semibold text-base tracking-wider mb-5">
          Upload EPCIS File
        </h3>
        <ul className="text-left">
          <li className="inline-block text-xs leading-8 w-full mb-4">
            <label className="text-labelclr font-semibold text-sm tracking-wider block">
              EPCIS XML File
              <span className="text-xs md:text-sm text-hlred ml-1">*</span>
            </label>
            <input
              id="epcis_upload"
              name="epcis_upload"
              type="file"
              accept=".xml"
              ref={register({ required: true })}
              onChange={handleFileChange}
              className="border focus:outline-none h-11 rounded-md pl-2 font-normal w-full text-fogotpwsd_login text-sm"
            />
            {fileValid && selectedFile && (
              <span className="block mt-1 text-xs text-green-600 font-medium">
                {selectedFile.name}
              </span>
            )}
          </li>
          <li className="inline-block text-xs leading-8 w-full text-center mt-2">
            <button
              type="button"
              onClick={handleUpload}
              disabled={loading || !fileValid}
              className="rounded font-medium h-10 text-white w-auto px-6 bg-header_bg mb-3 mr-2 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Upload
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="rounded font-medium h-10 text-white w-auto px-4 bg-secondary bg-login_btn mb-3 focus:outline-none"
            >
              Cancel
            </button>
          </li>
        </ul>
      </div>
    </Modal>
  );
};

export default EpcisUploadModal;
